import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const EMPTY = {
  title: "", description: "", category: "", duration: "12 weeks", status: "draft",
  offer_id: "", display_order: 0, completion_requirement: "all_modules",
  intake_questions: [], modules: [],
};
const SAMPLE_MODULES = `[
  {
    "title": "Week 1 — Foundations",
    "content": "Educational guidance for this module...",
    "tasks": ["Action step 1", "Action step 2"],
    "checkins": ["How is your energy this week?"],
    "reflections": ["What changed for you this week?"],
    "resources": [{"label": "Guide PDF", "url": "https://..."}],
    "practitioner_checkpoint": false
  }
]`;

export default function ClinicPrograms() {
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(null);
  const [modulesJson, setModulesJson] = useState(SAMPLE_MODULES);
  const [enroll, setEnroll] = useState({ template_id: "", client_email: "" });
  const [msg, setMsg] = useState("");

  // AI Clinical Content Studio state
  const [aiType, setAiType] = useState("program");
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPlan, setAiPlan] = useState(null); // protocol/wellness_plan draft (clinic_protocols shape)
  const [aiPlanKind, setAiPlanKind] = useState("protocol");
  const [planClientEmail, setPlanClientEmail] = useState("");
  const [planSaving, setPlanSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/programs/templates"); setTemplates(data.templates || []); }
    catch { setMsg("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ ...EMPTY }); setModulesJson(SAMPLE_MODULES); };
  const openEdit = (t) => { setForm({ ...t }); setModulesJson(JSON.stringify(t.modules || [], null, 2)); };

  const generateAI = async () => {
    if (!aiPrompt.trim()) { setMsg("Enter a prompt for the AI Studio."); return; }
    setAiBusy(true); setMsg(""); setAiPlan(null);
    try {
      const { data } = await api.post("/ai-studio/generate", { prompt: aiPrompt, type: aiType });
      const draft = data.draft || {};
      if (aiType === "program") {
        // Populate the Program Template editor with the editable draft
        setForm({
          ...EMPTY,
          title: draft.title || "",
          description: draft.description || "",
          category: draft.category || "",
          duration: draft.duration || "12 weeks",
          completion_requirement: draft.completion_requirement || "all_modules",
          intake_questions: draft.intake_questions || [],
          status: "draft",
        });
        setModulesJson(JSON.stringify(draft.modules || [], null, 2));
        setMsg("AI draft generated — " + (data.disclaimer || "review required") + ". Review & edit, then Save.");
      } else {
        // Protocol / Wellness Plan → clinic_protocols shape, saved via Protocol Builder
        setAiPlanKind(aiType);
        setAiPlan({
          title: draft.title || "",
          summary: draft.summary || "",
          herbal_recommendations: draft.herbal_recommendations || [],
          supplement_recommendations: draft.supplement_recommendations || [],
          service_recommendations: draft.service_recommendations || [],
          followup_schedule: draft.followup_schedule || [],
        });
        setMsg("AI draft generated — " + (data.disclaimer || "review required") + ". Review, add a client email, then Save to Protocol Builder.");
      }
    } catch (e) {
      setMsg(e?.response?.data?.detail || "AI generation failed — please retry.");
    } finally { setAiBusy(false); }
  };

  const savePlan = async () => {
    if (!aiPlan?.title || !planClientEmail.trim()) { setMsg("Client email and title are required to save."); return; }
    setPlanSaving(true); setMsg("");
    try {
      await api.post("/clinic/admin/protocols", {
        ...aiPlan,
        client_email: planClientEmail.trim(),
        status: "draft",
      });
      setAiPlan(null); setPlanClientEmail(""); setAiPrompt("");
      setMsg((aiPlanKind === "wellness_plan" ? "Wellness Plan" : "Protocol") + " saved as a draft in the Protocol Builder.");
    } catch (e) {
      setMsg(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Save failed (Protocol Builder requires Pro tier).");
    } finally { setPlanSaving(false); }
  };

  const save = async () => {
    setMsg("");
    let modules;
    try { modules = JSON.parse(modulesJson || "[]"); if (!Array.isArray(modules)) throw new Error(); }
    catch { setMsg("Modules must be valid JSON array"); return; }
    const payload = { ...form, modules, intake_questions: form.intake_questions || [] };
    try {
      if (form.id) await api.put(`/programs/templates/${form.id}`, payload);
      else await api.post("/programs/templates", payload);
      setForm(null); await load(); setMsg("Template saved.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Save failed"); }
  };

  const doEnroll = async () => {
    setMsg("");
    try {
      const { data } = await api.post("/programs/enrollments", enroll);
      setMsg(data.already ? "Client already enrolled." : "Client enrolled.");
    } catch (e) { setMsg(e?.response?.data?.detail || "Enroll failed"); }
  };

  const input = "mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";

  return (
    <div className="min-h-screen bg-[#FBF8F2]">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 h-14 flex items-center justify-between">
          <span className="font-display text-lg text-slate-900">KISSED Clinic OS · Program Engine</span>
          <Link to="/admin" className="text-xs text-purple-900 hover:underline">← Admin Hub</Link>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-6 py-8" data-testid="clinic-programs-admin">
        {msg && <div className="mb-4 text-sm text-purple-900 bg-purple-50 rounded-lg px-3 py-2" data-testid="programs-msg">{msg}</div>}

        {!form ? (
          <>
            {/* ── AI Clinical Content Studio ── */}
            <div className="bg-white rounded-xl border border-purple-200 p-6 mb-8" data-testid="ai-studio-panel">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-purple-900 font-display text-lg">✦ Create with AI</span>
                <span className="text-[10px] uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-2 py-0.5">Practitioner review required</span>
              </div>
              <p className="text-xs text-slate-500 mb-4">Generate an editable draft. Nothing is saved, published, or assigned until you review and click Save.</p>
              <div className="flex flex-wrap gap-2 mb-3" data-testid="ai-type-toggle">
                {[["program", "Interactive Program"], ["protocol", "Protocol"], ["wellness_plan", "Wellness Plan"]].map(([v, label]) => (
                  <button key={v} onClick={() => { setAiType(v); setAiPlan(null); }}
                    className={`text-xs rounded-full px-4 py-1.5 border ${aiType === v ? "bg-purple-900 text-white border-purple-900" : "bg-white text-slate-600 border-slate-300"}`}
                    data-testid={`ai-type-${v}`}>{label}</button>
                ))}
              </div>
              <textarea
                className={input + " min-h-[80px]"}
                rows={3}
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder={aiType === "program"
                  ? "e.g. A 12-week Hair & Scalp Restoration Program with weekly education, daily scalp-care actions, hydration, nutrition, herbal support, journaling, check-ins and practitioner checkpoints."
                  : aiType === "wellness_plan"
                    ? "e.g. A stress & sleep wellness plan focused on evening wind-down, hydration, gentle movement and mindful practices."
                    : "e.g. A clinical protocol for postpartum hair shedding with herbal and supplement support and a 6-week follow-up cadence."}
                data-testid="ai-prompt-input"
              />
              <button onClick={generateAI} disabled={aiBusy}
                className="mt-3 rounded-full bg-purple-900 text-white text-sm px-5 py-2 disabled:opacity-50" data-testid="ai-generate-btn">
                {aiBusy ? "Generating…" : "Generate Draft"}
              </button>

              {aiPlan && (
                <div className="mt-5 border-t border-slate-200 pt-4" data-testid="ai-plan-draft">
                  <div className="text-sm font-medium text-slate-900">{aiPlanKind === "wellness_plan" ? "Wellness Plan" : "Protocol"} draft — review & edit</div>
                  <label className="text-sm block mt-3">Title<input className={input} value={aiPlan.title} onChange={(e) => setAiPlan(p => ({ ...p, title: e.target.value }))} data-testid="ai-plan-title" /></label>
                  <label className="text-sm block mt-3">Summary<textarea className={input} rows={3} value={aiPlan.summary} onChange={(e) => setAiPlan(p => ({ ...p, summary: e.target.value }))} data-testid="ai-plan-summary" /></label>
                  <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-wide text-slate-500">
                    <span className="bg-slate-100 rounded-full px-2 py-0.5">{aiPlan.herbal_recommendations?.length || 0} herbal</span>
                    <span className="bg-slate-100 rounded-full px-2 py-0.5">{aiPlan.supplement_recommendations?.length || 0} supplements</span>
                    <span className="bg-slate-100 rounded-full px-2 py-0.5">{aiPlan.service_recommendations?.length || 0} services</span>
                    <span className="bg-slate-100 rounded-full px-2 py-0.5">{aiPlan.followup_schedule?.length || 0} follow-ups</span>
                  </div>
                  <label className="text-sm block mt-3">Client email (to save this draft)<input className={input} value={planClientEmail} onChange={(e) => setPlanClientEmail(e.target.value)} placeholder="client@example.com" data-testid="ai-plan-email" /></label>
                  <p className="text-xs text-slate-400 mt-1">Full recommendation editing is available in the Protocol Builder after saving.</p>
                  <div className="mt-4 flex gap-2">
                    <button onClick={savePlan} disabled={planSaving} className="rounded-full bg-purple-900 text-white text-sm px-5 py-2 disabled:opacity-50" data-testid="ai-plan-save">{planSaving ? "Saving…" : "Save to Protocol Builder"}</button>
                    <button onClick={() => setAiPlan(null)} className="rounded-full border border-slate-300 text-sm px-5 py-2">Discard</button>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between mb-4">
              <h1 className="font-display text-2xl text-slate-900">Program Templates</h1>
              <button onClick={openNew} className="rounded-full bg-purple-900 text-white text-sm px-4 py-2" data-testid="template-new">+ New Template</button>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 divide-y mb-8">
              {templates.map((t) => (
                <div key={t.id} className="p-4 flex items-center gap-4" data-testid={`template-row-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 truncate">{t.title}</div>
                    <div className="text-xs text-slate-500">{t.status} · {(t.modules || []).length} modules · {t.duration} {t.offer_id ? "· linked to offer" : ""}</div>
                  </div>
                  <button onClick={() => openEdit(t)} className="text-xs text-purple-900 hover:underline" data-testid={`template-edit-${t.id}`}>Edit</button>
                </div>
              ))}
              {templates.length === 0 && <div className="p-6 text-sm text-slate-500">No templates yet.</div>}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-xl">
              <h2 className="font-display text-lg text-slate-900 mb-3">Manually Enroll a Client</h2>
              <label className="text-sm">Template<select className={input} value={enroll.template_id} onChange={(e)=>setEnroll(s=>({...s,template_id:e.target.value}))} data-testid="enroll-template"><option value="">Select…</option>{templates.map(t=><option key={t.id} value={t.id}>{t.title}</option>)}</select></label>
              <label className="text-sm block mt-3">Client email<input className={input} value={enroll.client_email} onChange={(e)=>setEnroll(s=>({...s,client_email:e.target.value}))} data-testid="enroll-email" placeholder="client@example.com" /></label>
              <button onClick={doEnroll} className="mt-4 rounded-full bg-purple-900 text-white text-sm px-5 py-2" data-testid="enroll-submit">Enroll Client</button>
            </div>
          </>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-3xl">
            <h1 className="font-display text-xl text-slate-900 mb-4">{form.id ? "Edit Template" : "New Template"}</h1>
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="text-sm sm:col-span-2">Title<input className={input} value={form.title} onChange={(e)=>set("title",e.target.value)} data-testid="tpl-title" /></label>
              <label className="text-sm sm:col-span-2">Description<textarea className={input} rows={2} value={form.description} onChange={(e)=>set("description",e.target.value)} /></label>
              <label className="text-sm">Category<input className={input} value={form.category} onChange={(e)=>set("category",e.target.value)} /></label>
              <label className="text-sm">Duration<input className={input} value={form.duration} onChange={(e)=>set("duration",e.target.value)} /></label>
              <label className="text-sm">Status<select className={input} value={form.status} onChange={(e)=>set("status",e.target.value)}><option>draft</option><option>active</option><option>archived</option></select></label>
              <label className="text-sm">Linked Offer ID (optional)<input className={input} value={form.offer_id} onChange={(e)=>set("offer_id",e.target.value)} placeholder="clinic-offer id" /></label>
              <label className="text-sm sm:col-span-2">Modules (JSON — AI Studio can generate this)<textarea className={input+" font-mono text-xs"} rows={12} value={modulesJson} onChange={(e)=>setModulesJson(e.target.value)} data-testid="tpl-modules" /></label>
            </div>
            <div className="mt-6 flex gap-2">
              <button onClick={save} className="rounded-full bg-purple-900 text-white text-sm px-5 py-2" data-testid="tpl-save">Save Template</button>
              <button onClick={() => setForm(null)} className="rounded-full border border-slate-300 text-sm px-5 py-2">Cancel</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
